if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          shareLinks: this.querySelectorAll('[data-share-link]'),
        };

        // Absolute product (or selected variant) URL rendered by Liquid. Used as the base
        // for every share target, and as the fallback when the element is rendered
        // somewhere the address bar is not a product page.
        this.baseUrl = this.dataset.shareUrl || window.location.href;
        this.shareTitle = this.dataset.shareTitle || document.title;

        // Kept for backwards compatibility: `urlToShare` was a public field on the previous
        // implementation.
        this.urlToShare = this.baseUrl;

        // Whether the Web Share API exists at all. The dedicated Share control uses this, so
        // the device's own sheet opens wherever the browser offers one — desktop Chrome and
        // Edge included, where it surfaces Mail, WhatsApp and the rest via the OS.
        this.hasWebShare = typeof navigator.share === 'function';

        // The named networks are stricter: on a desktop pointer the OS sheet is a poor
        // substitute for "share to TikTok", so that path stays reserved for touch devices and
        // copies the link everywhere else.
        this.supportsNativeShare = this.hasWebShare && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

        this.addEventListener('click', this.handleClick.bind(this));

        // `variant:update` bubbles to the document and carries the new variant id, so the
        // share targets stay in step with the variant picker.
        document.addEventListener('variant:update', this.handleVariantUpdate.bind(this));

        this.refresh();
      }

      /**
       * The URL to share right now.
       *
       * `variant-picker.js` writes the selected variant into the address bar, so on a product
       * page the location is the source of truth. Only the `variant` parameter is carried over
       * — campaign and click-id parameters are dropped so they are not shared onwards.
       * @returns {string}
       */
      currentUrl() {
        try {
          if (window.location.pathname.includes('/products/')) {
            const url = new URL(window.location.pathname, window.location.origin);
            const variant = new URLSearchParams(window.location.search).get('variant');

            if (variant) url.searchParams.set('variant', variant);

            return url.toString();
          }
        } catch (error) {
          // Fall through to the Liquid-rendered URL.
        }

        return this.baseUrl;
      }

      /** Points the network links at the current URL. */
      refresh() {
        const url = this.currentUrl();
        const encodedUrl = encodeURIComponent(url);

        this.urlToShare = url;

        this.elements.shareLinks.forEach((link) => {
          const network = link.dataset.shareLink;

          if (network === 'x') {
            link.href = `https://x.com/intent/post?text=${encodeURIComponent(this.shareTitle)}&url=${encodedUrl}`;
          } else if (network === 'facebook') {
            link.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          }
        });
      }

      handleVariantUpdate(event) {
        const detail = event.detail;
        const variantId = detail?.resource?.id;
        const newProductUrl = detail?.data?.newProduct?.url;

        if (!variantId && !newProductUrl) return;

        try {
          const base = new URL(newProductUrl || this.baseUrl, window.location.origin);

          if (variantId) {
            base.searchParams.set('variant', variantId);
          } else {
            base.searchParams.delete('variant');
          }

          this.baseUrl = base.toString();
        } catch (error) {
          // Leave the existing base URL in place.
        }

        this.refresh();
      }

      handleClick(event) {
        const trigger = event.target.closest('[data-share-action]');

        if (!trigger || !this.contains(trigger)) return;

        switch (trigger.dataset.shareAction) {
          case 'native':
            event.preventDefault();
            this.share(trigger.dataset.shareNetwork);
            break;
          case 'system':
            event.preventDefault();
            this.share(undefined, true);
            break;
          case 'copy':
            event.preventDefault();
            this.copy();
            break;
          default:
            break;
        }
      }

      /**
       * Hands off to the device's share sheet, falling back to copying the link.
       * @param {string} [network] - Network name, used in the copy confirmation.
       * @param {boolean} [anyPointer] - Allow the sheet on desktop pointers too. Set by the
       *   dedicated Share control, whose whole purpose is the device's own options.
       */
      async share(network, anyPointer = false) {
        const url = this.currentUrl();

        if (anyPointer ? this.hasWebShare : this.supportsNativeShare) {
          try {
            await navigator.share({ title: this.shareTitle, text: this.shareTitle, url });
            return;
          } catch (error) {
            // Dismissing the share sheet rejects with AbortError. That is a deliberate
            // cancellation, not a failure, so nothing should happen.
            if (error && error.name === 'AbortError') return;
          }
        }

        this.copy(network);
      }

      /**
       * Copies the current URL to the clipboard.
       * @param {string} [network] - Network name, used in the confirmation message.
       */
      async copy(network) {
        const url = this.currentUrl();
        let copied = false;

        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(url);
            copied = true;
          }
        } catch (error) {
          copied = false;
        }

        // The async clipboard needs a secure context and permission. Where it is unavailable,
        // the legacy copy command still works.
        if (!copied) copied = this.legacyCopy(url);

        if (!copied) {
          this.showMessage(this.dataset.copyError);
          return;
        }

        if (network) {
          const hint = this.dataset.copyPasteHint || '';
          this.showMessage(hint.includes('[app]') ? hint.replace('[app]', network) : `${hint} ${network}`.trim());
        } else {
          this.showMessage(this.dataset.copySuccess);
        }
      }

      /**
       * Copies via a throwaway off-screen field, for browsers without the async clipboard.
       * @param {string} url - The URL to copy.
       * @returns {boolean} Whether the copy succeeded.
       */
      legacyCopy(url) {
        const field = document.createElement('textarea');

        field.value = url;
        field.setAttribute('readonly', '');
        field.setAttribute('aria-hidden', 'true');
        field.style.position = 'fixed';
        field.style.top = '-9999px';
        field.style.opacity = '0';

        document.body.appendChild(field);

        try {
          field.select();
          field.setSelectionRange(0, url.length);

          return document.execCommand('copy');
        } catch (error) {
          return false;
        } finally {
          field.remove();
        }
      }

      showMessage(text) {
        if (!text || !this.elements.successMessage) return;

        this.elements.successMessage.textContent = text;
        this.elements.successMessage.classList.remove('hidden');

        clearTimeout(this.messageTimeout);
        this.messageTimeout = setTimeout(() => this.resetMessage(), 4000);
      }

      resetMessage() {
        if (!this.elements.successMessage) return;

        this.elements.successMessage.textContent = '';
        this.elements.successMessage.classList.add('hidden');
      }

      /**
       * Replaces the base share URL. Retained from the previous implementation for callers
       * outside this file.
       * @param {string} url - The new absolute URL to share.
       */
      updateUrl(url) {
        this.baseUrl = url;
        this.refresh();
      }
    }
  );
}
